"use client";

import { AsyncResult, err, ok } from "@jace/api";
import { EventEmitter, unwrapOr } from "@zetalabs/core";
import { router } from "../../../router";
import { AuthSignedInError } from "../../errors";
import { AuthEvent, ClientEvent } from "../../events";
import { AuthEventHanlder, SignInOptions, SignOutOptions } from "../../types";
import { GoogleAuthFlowType } from "./types";

const SIMPLE_FLOW_KEY = "simple_flow";

interface GoogleAuthClientConfig {
	appUrl: string;
	flowType: GoogleAuthFlowType;
	fallbackRedirectUrl?: string;
	oneTap?: boolean;
}

interface GoogleAuthOverrides {
	flowType?: GoogleAuthFlowType;
}

// NOTE: Consider moving flowType to the `signIn` method,
// since we are flip flopping between the simple and the full flow.
export class GoogleAuthClient {
	readonly name = "google";
	readonly authUrl: string;
	readonly fallbackRedirectUrl?: string;

	private emitter = new EventEmitter<ClientEvent>();

	constructor(readonly config: GoogleAuthClientConfig) {
		const searchParams = new URLSearchParams(new URL(config.appUrl).search);
		const url = new URL("/oauth/google", config.appUrl);

		if (config.flowType === "simple") {
			searchParams.set("simple_flow", "true");
		}

		url.search = searchParams.toString();

		this.authUrl = url.toString();
		this.fallbackRedirectUrl = config.fallbackRedirectUrl;
	}

	signIn = (
		opts?: SignInOptions & GoogleAuthOverrides,
	): AsyncResult<void, AuthSignedInError> => {
		return new Promise((resolve, reject) => {
			try {
				const url = new URL(this.authUrl);

				if (opts?.searchParams) {
					for (const [key, value] of opts.searchParams) {
						console.log(
							`Overwrite searchParams: [key]: ${key}, [value]: ${value}`,
						);
						url.searchParams.set(key, value);
					}
				}

				const redirectUrl = opts?.redirectUrl || this.fallbackRedirectUrl;

				if (redirectUrl) {
					url.searchParams.set("redirect_url", redirectUrl);
				}

				if (opts?.flowType) {
					switch (opts.flowType) {
						case "simple":
							url.searchParams.set(SIMPLE_FLOW_KEY, "true");
							break;
						case "full":
							url.searchParams.delete(SIMPLE_FLOW_KEY);
							break;
						default:
							throw new Error(
								`Invalid flow type: ${opts.flowType}. Expected one of: simple, full`,
							);
					}
				}

				this.emitter.emit(
					"auth.sign_in.success",
					new AuthEvent("auth.sign_in.success", {
						reason: `Redirecting to simplified OAuth: ${url.toString()}`,
					}),
				);

				router.push(url);

				return resolve(ok(undefined));
			} catch (e) {
				const error = unwrapOr(e, new AuthSignedInError());

				this.emitter.emit(
					"auth.sign_in.error",
					new AuthEvent("auth.sign_in.error", {
						reason: error.message,
					}),
				);

				return reject(err(error));
			}
		});
	};

	signOut = (_opts: SignOutOptions): AsyncResult<void> => {
		throw new Error("Method not implemented.");
	};

	on = (_event: ClientEvent, _listener: AuthEventHanlder) => {
		this.emitter.on(_event, _listener);
		throw new Error("Method not implemented.");
	};

	withOneTap = (): boolean => {
		return this.config.oneTap !== undefined ? this.config.oneTap : false;
	};
}
